import React from 'react';
import { Drawer, Typography, IconButton, Box, Chip, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const statusColors = {
    completed: { bg: 'rgba(76,175,80,0.15)', color: '#66bb6a', icon: CheckCircleIcon },
    missed: { bg: 'rgba(244,67,54,0.15)', color: '#ef5350', icon: CancelIcon },
    pending: { bg: 'rgba(255,152,0,0.15)', color: '#ffa726', icon: AccessTimeIcon },
    partial: { bg: 'rgba(33,150,243,0.15)', color: '#64b5f6', icon: AccessTimeIcon },
};

const InfoRow = ({ label, value }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.5} sx={{ borderBottom: '1px solid #222' }}>
        <Typography variant="caption" sx={{ color: '#888' }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>{value || '—'}</Typography>
    </Box>
);

export default function ResponseDetailsDrawer({ open, onClose, response }) {
    if (!response) return null;

    const st = statusColors[response.status] || statusColors.Pending;
    const StatusIcon = st.icon;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: '#141414',
                    color: '#fff',
                    width: { xs: '100%', sm: '400px' },
                    p: 0,
                    top: '64px',
                    height: 'calc(100% - 64px)',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header */}
            <Box display="flex" alignItems="center" p={3} pb={2} sx={{ borderBottom: '1px solid #222' }}>
                <IconButton onClick={onClose} sx={{ color: '#aaa', border: '1px solid #333', borderRadius: '8px', p: 1, mr: 2, '&:hover': { color: '#fff', backgroundColor: '#333' } }}>
                    <ArrowBackIosNewIcon sx={{ fontSize: '16px' }} />
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">Response Details</Typography>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
                {/* User Info */}
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold' }}>
                        {response.customer?.name?.charAt(0) || 'U'}
                    </Box>
                    <Box>
                        <Typography variant="body2" fontWeight="bold">{response.customer?.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#777' }}>{response.customer?.email || 'user@example.com'}</Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                        <Chip
                            icon={<StatusIcon sx={{ fontSize: 14, color: `${st.color} !important` }} />}
                            label={response.status}
                            size="small"
                            sx={{ backgroundColor: st.bg, color: st.color, fontSize: '0.7rem', borderRadius: '6px' }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ borderColor: '#222', mb: 2 }} />

                {/* Task Info */}
                <Typography variant="caption" sx={{ color: '#3c50b4', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5, display: 'block' }}>Task Information</Typography>
                <InfoRow label="Task Name" value={response.dailyTask?.title} />
                <InfoRow label="Task Type" value={response.dailyTask?.taskType} />
                <InfoRow label="Assigned Date" value={(response.createdAt || response.date) ? new Date(response.createdAt || response.date).toLocaleDateString() : '—'} />
                <InfoRow label="Due Date" value={response.date ? new Date(response.date).toLocaleDateString() : '—'} />

                <Divider sx={{ borderColor: '#222', my: 2 }} />

                {/* Response */}
                <Typography variant="caption" sx={{ color: '#3c50b4', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5, display: 'block' }}>User Response</Typography>
                <InfoRow label="Response Date" value={response.date ? new Date(response.date).toLocaleDateString() : '—'} />
                <InfoRow label="Duration / Qty" value={response.value || '—'} />

                {response.notes && (
                    <Box mt={2}>
                        <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block' }}>Notes</Typography>
                        <Box sx={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '8px', p: 2 }}>
                            <Typography variant="body2" sx={{ color: '#ccc', fontSize: '0.85rem' }}>{response.notes}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
} 